import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "components/ui/Button";
import StatCard from "components/ui/StatCard";
import ConfirmDialog from "components/ui/ConfirmDialog";
import Icon from "components/AppIcon";
import ClientTable from "./components/ClientTable";
import AddClientModal from "./components/AddClientModal";
import ClientLedgerModal from "./components/ClientLedgerModal";
import LoadingSkeleton from "./components/LoadingSkeleton";
import { useGetClients } from "./useGetClients";
import { useCreateClient } from "./useCreateClient";
import { useUpdateClient } from "./useUpdateClient";
import { useDeleteClient } from "./useDeleteClient";
import { countActive, countByBalanceType } from "./logic/stats";

const ClientManagementContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");

  // ── Data + mutations ─────────────────────────────────────────────────────
  const { data: clients = [], isLoading, isError, error } = useGetClients();
  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient();
  const deleteMutation = useDeleteClient();

  // ── UI state ─────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);
  const [ledgerClient, setLedgerClient] = useState(null);
  const itemsPerPage = 10;

  // Reset paging when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return clients;
    const term = searchTerm.toLowerCase();
    return clients.filter(
      (c) =>
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.contact?.includes(searchTerm) ||
        c.taxId?.includes(searchTerm),
    );
  }, [searchTerm, clients]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = [
    {
      label: t("clients.stats.total"),
      value: clients.length,
      icon: "Users",
      color: "text-blue-600 bg-blue-100 dark:bg-blue-900/20",
    },
    {
      label: t("clients.stats.active"),
      value: countActive(clients),
      icon: "CheckCircle2",
      color: "text-green-600 bg-green-100 dark:bg-green-900/20",
    },
    {
      label: t("clients.stats.clientsOweYou"),
      value: countByBalanceType(clients, "owes_you"),
      icon: "TrendingUp",
      color: "text-primary bg-primary/10",
    },
    {
      label: t("clients.stats.youOwe"),
      value: countByBalanceType(clients, "you_owe"),
      icon: "TrendingDown",
      color: "text-red-600 bg-red-100 dark:bg-red-900/20",
    },
  ];

  // ── Handlers ──────────────────────────────────────────────────────────────
  const closeModal = () => {
    setIsAddModalOpen(false);
    setEditingClient(null);
  };

  const handleSave = (clientData) => {
    if (editingClient) {
      updateMutation.mutate(
        { id: editingClient.id, ...clientData },
        {
          onSuccess: () => {
            toast.success(t("clients.successUpdate"));
            closeModal();
          },
          onError: (err) => {
            toast.error(err?.message || t("clients.failedUpdate"));
          },
        },
      );
    } else {
      createMutation.mutate(clientData, {
        onSuccess: () => {
          toast.success(t("clients.successAdd"));
          closeModal();
        },
        onError: (err) => {
          toast.error(err?.message || t("clients.failedAdd"));
        },
      });
    }
  };

  const handleConfirmDelete = () => {
    if (!deletingClient) return;
    deleteMutation.mutate(deletingClient.id, {
      onSuccess: () => {
        toast.success(t("clients.deleteSuccess", { name: deletingClient.name }));
        setDeletingClient(null);
      },
      onError: (err) => {
        toast.error(err?.message || t("clients.failedDelete"));
      },
    });
  };

  if (isLoading) return <LoadingSkeleton isCollapsed={isCollapsed} />;

  if (isError) {
    return (
      <div
        className={`transition-all duration-300 ease-in-out ${
          isCollapsed ? "md:ms-16" : "md:ms-64"
        } p-8`}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Icon
              name="AlertTriangle"
              size={48}
              className="mx-auto text-red-500 mb-4"
            />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t("clients.failedLoad")}
            </h3>
            <p className="text-muted-foreground mb-4">
              {error?.message || t("clients.failedLoadDescription")}
            </p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t("actions.retry")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <main className="p-4 md:p-6">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("clients.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("clients.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="md"
                iconName="Download"
                className="shadow-sm hover:shadow-lg transition-all duration-300"
              >
                {t("actions.export")}
              </Button>
              <Button
                variant="default"
                size="md"
                iconName="Plus"
                className="shadow-sm hover:shadow-lg transition-all duration-300"
                onClick={() => {
                  setEditingClient(null);
                  setIsAddModalOpen(true);
                }}
              >
                {t("actions.addClient")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* ── Stats Row ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </motion.div>

        {/* ── Search ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-card rounded-xl p-6 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={20}
              className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder={t("clients.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full ps-10 pe-10 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
        </motion.div>

        {/* ── Table ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <ClientTable
            clients={paginated}
            onEdit={(client) => {
              setEditingClient(client);
              setIsAddModalOpen(true);
            }}
            onDelete={(client) => setDeletingClient(client)}
            onViewLedger={(client) => setLedgerClient(client)}
          />
        </motion.div>

        {/* ── Empty State ── */}
        {filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-6"
          >
            <Icon
              name="Users"
              size={48}
              className="mx-auto mb-4 text-muted-foreground"
            />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("clients.emptyTitle")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm
                ? t("common.adjustSearchCriteria")
                : t("clients.emptyDescription")}
            </p>
            {!searchTerm && (
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => {
                  setEditingClient(null);
                  setIsAddModalOpen(true);
                }}
              >
                {t("actions.addClient")}
              </Button>
            )}
          </motion.div>
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {t("common.showingRange", {
                  from: (currentPage - 1) * itemsPerPage + 1,
                  to: Math.min(currentPage * itemsPerPage, filtered.length),
                  total: filtered.length,
                  entity: t("entities.clients"),
                })}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronLeft"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                />
                <span className="px-3 py-1 text-sm text-foreground">
                  {t("common.pageOf", { current: currentPage, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronRight"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ── Add / Edit Modal ── */}
      <AnimatePresence>
        {isAddModalOpen && (
          <AddClientModal
            existingClient={editingClient}
            onSave={handleSave}
            onClose={closeModal}
            isSaving={isSaving}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {ledgerClient && (
          <ClientLedgerModal
            client={ledgerClient}
            onClose={() => setLedgerClient(null)}
          />
        )}
      </AnimatePresence>

      {/* ── Delete Confirmation ── */}
      <ConfirmDialog
        isOpen={!!deletingClient}
        title={t("clients.deleteTitle")}
        message={
          deletingClient
            ? t("clients.deleteMessage", { name: deletingClient.name })
            : ""
        }
        confirmLabel={t("actions.delete")}
        variant="danger"
        isLoading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          if (!deleteMutation.isPending) setDeletingClient(null);
        }}
      />
    </div>
  );
};

const ClientManagement = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <ClientManagementContent />
    </div>
  </SidebarProvider>
);

export default ClientManagement;
